'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useI18n } from '@/i18n/LocaleProvider';


export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPct = (clientX / innerWidth - 0.5) * 2;
      const yPct = (clientY / innerHeight - 0.5) * 2;
      const blobs = hero.querySelectorAll<HTMLElement>('.parallax-blob');
      blobs.forEach((blob, i) => {
        const factor = (i + 1) * 12;
        blob.style.transform = `translate(${xPct * factor}px, ${yPct * factor}px)`;
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-36 pb-16">
      
      {/* Ambient background blobs */}
      <div className="parallax-blob absolute top-1/4 left-1/4 w-96 h-96 blob-primary opacity-60 pointer-events-none transition-transform duration-300 ease-out" />
      <div className="parallax-blob absolute bottom-1/3 right-1/4 w-80 h-80 blob-accent opacity-50 pointer-events-none transition-transform duration-300 ease-out" />
      <div className="parallax-blob absolute top-1/2 right-1/3 w-64 h-64 blob-primary opacity-30 pointer-events-none transition-transform duration-300 ease-out" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(124,58,237,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px'
        }} />
      

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-7xl mx-auto w-full">
        {/* Brand cards: the logo's gold card with the rest of the deck fading out */}
        <img
          src="/assets/images/hero-cards.svg"
          alt=""
          aria-hidden
          width={64}
          height={29}
          className="mx-auto mb-8 h-auto w-[min(440px,80vw)] select-none pointer-events-none drop-shadow-[0_12px_40px_rgba(124,58,237,0.35)]"
          style={{ animation: 'heroReveal 0.9s cubic-bezier(0.16,1,0.3,1) 0s both' }} />

        {/* Eyebrow */}
        <div
          className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 mb-8"
          style={{ animation: 'heroReveal 0.8s cubic-bezier(0.16,1,0.3,1) 0.1s both' }}>
          
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse-glow" />
          <span className="text-xs font-semibold tracking-widest uppercase text-accent">
            {t.hero.eyebrow}
          </span>
        </div>

        {/* Main headline */}
        <h1
          className="font-display font-extrabold tracking-tighter leading-none mb-6 select-none"
          style={{
            fontSize: 'clamp(3.5rem, 13vw, 12rem)',
            animation: 'heroReveal 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s both'
          }}>
          
          <span className="hero-text-gradient block">TCG</span>
          <span className="accent-text-gradient block">SECRET</span>
        </h1>

        {/* Tagline */}
        <p
          className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10"
          style={{ animation: 'heroReveal 0.9s cubic-bezier(0.16,1,0.3,1) 0.35s both' }}>
          
          {t.hero.tagline}
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          style={{ animation: 'heroReveal 0.9s cubic-bezier(0.16,1,0.3,1) 0.45s both' }}>
          
          {/* Primary CTA with spinning border beam */}
          <Link
            href="/category"
            className="group relative inline-flex items-center gap-3 rounded-full px-3 py-2 pr-7 overflow-hidden"
            style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)' }}>
            
            {/* Spinning border beam */}
            <div className="absolute inset-0 rounded-full overflow-hidden" style={{ padding: '1px', WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude', pointerEvents: 'none' }}>
              <div className="absolute -inset-full border-beam-spin" style={{ background: 'conic-gradient(from 0deg, transparent 0 300deg, #C8A94A 360deg)' }} />
            </div>
            <div className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #7C3AED, #C8A94A)' }}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
              </svg>
            </div>
            <span className="relative z-10 text-base font-semibold text-foreground group-hover:text-primary transition-colors">
              {t.hero.browseAll}
            </span>
          </Link>

          <Link
            href="/#about"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground border border-border hover:border-primary/40 transition-all duration-300">
            
            {t.hero.aboutShop}
            <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>

        {/* Store announcement */}
        <Link
          href="/#store"
          className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-2 text-xs sm:text-sm font-semibold text-accent hover:bg-accent/20 transition-colors"
          style={{ animation: 'heroReveal 0.9s cubic-bezier(0.16,1,0.3,1) 0.55s both' }}>
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse-glow" />
          {t.store.announcement}
          <span aria-hidden>→</span>
        </Link>

      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs tracking-widest uppercase text-muted-foreground">{t.hero.scroll}</span>
        <div className="w-px h-8 bg-gradient-to-b from-primary to-transparent" />
      </div>
    </section>);

}